import { makeAutoObservable, runInAction } from 'mobx'
import { AxiosError } from 'axios'
import { isLink, logger } from 'shared/lib'
import { KnowledgeBaseApi } from 'entities/KnowledgeBase'
import { MAX_FILES, STORAGE_IS_FULL } from 'pages/settings/pages/knowledgeBase/lib/constants'
import { type KnowledgeBaseDocumentsStore } from './knowledgeBaseDocumentsStore'

export class KnowledgeBaseUrlStore {
  private _url = ''
  private _isUploading = false
  private _isUploadViaUrlOpen = false
  private _errorMessages: Record<string, string> = {}

  constructor(private _documentsStore: KnowledgeBaseDocumentsStore) {
    makeAutoObservable(this)
  }

  get url() {
    return this._url
  }

  get isUploading() {
    return this._isUploading
  }

  get isUploadViaUrlOpen() {
    return this._isUploadViaUrlOpen
  }

  get errorMessages() {
    return this._errorMessages
  }

  setIsUploadViaUrlOpen = (isUploadViaUrlOpen: boolean) => {
    this._isUploadViaUrlOpen = isUploadViaUrlOpen
  }

  setIsUploading = (isUploading: boolean) => {
    this._isUploading = isUploading
  }

  setUrl = (url: string) => {
    this._url = url

    if (this._errorMessages.url) {
      this.setErrorMessage('url', '')
    }
  }

  setErrorMessage = (key: string, error: string) => {
    this._errorMessages[key] = error
  }

  handleImportUrl = async () => {
    const isUrl = isLink(this._url)
    const enterValidText = 'Please enter a valid file URL'

    if (!isUrl) {
      this.setErrorMessage('url', enterValidText)
      return
    }

    const knowledgeBase = this._documentsStore.knowledgeBase

    if (knowledgeBase && knowledgeBase.documentsCount === MAX_FILES) {
      this._documentsStore.notificationStore.addError(STORAGE_IS_FULL)
      return
    }

    try {
      runInAction(() => {
        this._isUploading = true
      })

      await KnowledgeBaseApi.importWebUrl(this.url)
      void this._documentsStore.loadDocuments()
    } catch (e) {
      if (e instanceof AxiosError && e.errors.getError('url')) {
        this.setErrorMessage('importUrl', 'Import failed. Please check the URL and try again.')
      }
      logger.error(e)
    } finally {
      runInAction(() => {
        this._isUploading = false
      })
      this.setIsUploadViaUrlOpen(false)
      this.setUrl('')
    }
  }

  handleResyncUrl = async (id: number) => {
    try {
      await KnowledgeBaseApi.resyncUrl(id)
      void this._documentsStore.loadDocuments()
    } catch (e) {
      logger.error(e)
    }
  }
}
